import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";
import "./CurrencyConverter.css";
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";

// Flag imports
import aeflag from "../../assets/images/flags/ae_flag.png";
import frflag from "../../assets/images/flags/fr_flag.png";
import lbflag from "../../assets/images/flags/lb_flag.svg";
import qaflag from "../../assets/images/flags/qa_flag.svg";
import saflag from "../../assets/images/flags/saudi_flag.webp";
import syflag from "../../assets/images/flags/syria_flag.webp";
import usflag from "../../assets/images/flags/us_flag.png";

const flagMap = {
  AED: aeflag,
  EUR: frflag,
  LBP: lbflag,
  QAR: qaflag,
  SAR: saflag,
  SYP: syflag,
  USD: usflag,
};

const QUICK_PICKS = ["LBP", "USD", "AED", "EUR"];

function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => {
      if (!ref.current || ref.current.contains(e.target)) return;
      onOutside();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside]);
}

const CurrencyDropdown = ({
  open,
  onClose,
  currencies,
  onSelect,
  selectedCode,
}) => {
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  useClickOutside(boxRef, onClose);

  const filtered = useMemo(() => {
    if (!query.trim()) return currencies;
    const q = query.toLowerCase();
    return currencies.filter(
      (c) =>
        c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q)
    );
  }, [currencies, query]);

  if (!open) return null;

  return (
    <div className="cc-dropdown-portal">
      <div ref={boxRef} className="cc-dropdown">
        <div className="cc-dropdown-header">
          <input
            autoFocus
            className="cc-search"
            placeholder="Search currency (e.g., USD, LBP, Euro)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="cc-quick-picks">
          {QUICK_PICKS.map((code) => {
            const c = currencies.find((x) => x.code === code);
            if (!c) return null;
            return (
              <button
                key={code}
                className={`cc-chip ${selectedCode === code ? "active" : ""}`}
                onClick={() => {
                  onSelect(c);
                  onClose();
                }}
              >
                <img src={c.flag} alt={c.code} className="cc-flag xs" />
                {c.code}
              </button>
            );
          })}
        </div>

        <div className="cc-options">
          {filtered.map((c) => (
            <button
              key={c.code}
              className={`cc-option ${selectedCode === c.code ? "selected" : ""}`}
              onClick={() => {
                onSelect(c);
                onClose();
              }}
            >
              <img src={c.flag} alt={c.code} className="cc-flag" />
              <div className="cc-option-text">
                <div className="cc-option-line">
                  <span className="cc-code">{c.code}</span>
                  <span className="cc-name">{c.name}</span>
                </div>
                <div className="cc-sub">
                  1 USD ≈ {new Intl.NumberFormat("en-US").format(c.rate)} {c.code}
                </div>
              </div>
            </button>
          ))}

          {filtered.length === 0 && (
            <div className="cc-empty">No currencies match “{query}”.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const CurrencyConverter = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [amount, setAmount] = useState("1200000");
  const [fromCurrency, setFromCurrency] = useState(null);
  const [toCurrency, setToCurrency] = useState("Winicoins");
  const [currencies, setCurrencies] = useState([]);
  const [winiToUsdRate, setWiniToUsdRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [showFrom, setShowFrom] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setErr("");

        // Fetch WiniCoin rate from app_settings table
        const { data: appSettings, error: settingsError } = await supabase
          .from("app_settings")
          .select("wini_to_usd_rate")
          .limit(1)
          .single();

        if (settingsError) {
          console.error("Error fetching Wini rate:", settingsError);
        } else {
          setWiniToUsdRate(appSettings?.wini_to_usd_rate || null);
        }

        // Fetch available currencies from currencies table
        const { data: currenciesData, error: currenciesError } = await supabase
          .from("currencies")
          .select("*")
          .order("iso");

        if (currenciesError) {
          console.error("Error fetching currencies:", currenciesError);
          setErr("Could not load rates. Please try again.");
          return;
        }

        const list = (currenciesData || [])
          .filter((c) => c.rate && c.name) // Filter out invalid entries
          .map((c) => ({
            code: c.iso,
            name: c.name,
            rate: c.rate, // how many local units per 1 USD
            USDToCoinExchangeRate: c.rate, // For compatibility
            flag: flagMap[c.iso] || usflag,
          }));

        // Sort: quick picks first, then alphabetically
        const sorted = list.sort((a, b) => {
          const aIdx = QUICK_PICKS.indexOf(a.code);
          const bIdx = QUICK_PICKS.indexOf(b.code);
          if (aIdx !== -1 || bIdx !== -1) {
            if (aIdx === -1) return 1;
            if (bIdx === -1) return -1;
            return aIdx - bIdx;
          }
          return a.code.localeCompare(b.code);
        });

        setCurrencies(sorted);

        // Default selected currency
        if (sorted.length > 0) {
          setFromCurrency(sorted.find((c) => c.code === "LBP") || sorted[0]);
        }
      } catch (e) {
        console.error(e);
        setErr("Could not load rates. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const numericAmount = useMemo(() => {
    const n = parseFloat((amount || "0").toString().replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  const converted = useMemo(() => {
    if (!fromCurrency || !winiToUsdRate) return 0;
    if (toCurrency === "Winicoins") {
      // Local currency -> USD -> WNC
      const usd = numericAmount / fromCurrency.rate;
      return usd / winiToUsdRate;
    } else {
      // WNC -> USD -> Local currency
      const usd = numericAmount * winiToUsdRate;
      return usd * fromCurrency.rate;
    }
  }, [fromCurrency, winiToUsdRate, numericAmount, toCurrency]);

  const approxUSD = useMemo(() => {
    if (!fromCurrency || !winiToUsdRate) return 0;
    if (toCurrency === "Winicoins") {
      // Show the USD value of the input amount
      return numericAmount / fromCurrency.rate;
    } else {
      // Show the USD value of the converted local amount
      return converted / fromCurrency.rate;
    }
  }, [fromCurrency, winiToUsdRate, numericAmount, toCurrency, converted]);

  const format = (n, max = 2) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: max,
    }).format(Number.isFinite(n) ? n : 0);

  const handleBack = () => navigate(-1);

  const handleSwitch = () => {
    // Toggle between “currency → WNC” and “WNC → currency”
    setToCurrency((prev) => (prev === "Winicoins" ? "Currency" : "Winicoins"));
  };

  return (
    <div className={`converter-page ${loading ? "loading" : ""} ${err ? "error" : ""}`}>
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Header */}
      <div className="converter-header">
        <button className="converter-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M12 19l-7-7 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1 className="converter-page-title">Currency Converter</h1>
      </div>

      {/* Banner */}
      <div className="cc-banner">
        <div className="cc-banner-glow" />
        <div className="cc-banner-content">
          <div className="cc-tag">Live</div>
          <div className="cc-banner-text">
            <strong>1 WNC</strong> ≈ <span className="cc-accent">
              {winiToUsdRate ? `$${format(winiToUsdRate, 6)} USD` : "—"}
            </span>
          </div>
          <div className="cc-subtle">
            Rates fetched for display purposes only.
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="converter-container">
        {/* Input card */}
        <div className="converter-card converter-input-card">
          <div className="converter-card-header">
            <div className="converter-card-title">
              Amount in {toCurrency === "Winicoins" ? (fromCurrency ? fromCurrency.code : 'Currency') : 'Winicoins'}
            </div>
            {fromCurrency && (
              <div className="cc-rate-pill">
                1 USD ≈ {format(fromCurrency.rate, 4)} {fromCurrency.code}
              </div>
            )}
          </div>

          <div className="converter-amount-row">
            <input
              type="text"
              inputMode="decimal"
              value={amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              onChange={(e) =>
                setAmount(e.target.value.replace(/[^\d.]/g, ""))
              }
              className="converter-amount-input"
              placeholder="0"
              aria-label="Amount to convert"
            />

            <button
              className="converter-currency-chip"
              onClick={() => setShowFrom(true)}
              aria-expanded={showFrom}
              aria-controls="currency-dropdown"
            >
              {fromCurrency && (
                <>
                  <img
                    src={fromCurrency.flag}
                    alt={fromCurrency.code}
                    className="converter-currency-flag"
                  />
                  <span className="converter-currency-code">
                    {fromCurrency.code}
                  </span>
                </>
              )}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M6 9l6 6 6-6"
                  stroke="#666"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="cc-helper-row">
            <div className="cc-helper">
              ≈ {format(approxUSD, 2)} <span className="cc-mono">USD</span>
            </div>
            <button className="converter-switch-button" onClick={handleSwitch}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  stroke="#475569"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {toCurrency === "Winicoins" ? "Switch to WNC → Local" : "Switch to Local → WNC"}
            </button>
          </div>
        </div>

        {/* Output card */}
        <div className="converter-card converter-output-card">
          <div className="converter-card-title">Converted amount</div>
          <div className="converter-converted-amount">
            {loading ? "—" : format(converted, toCurrency === "Winicoins" ? 6 : 2)}
          </div>
          <div className="converter-converted-currency">
            {toCurrency === "Winicoins"
              ? "Winicoins (WNC)"
              : `${fromCurrency?.code || ""}`}
          </div>

          <div className="cc-meta-row">
            <div className="cc-meta">
              {toCurrency === "Winicoins" ? (
                <>
                  Based on <b>1 WNC = {winiToUsdRate ? `$${format(winiToUsdRate, 6)}` : "—"} USD</b>
                </>
              ) : (
                <>
                  Using <b>1 USD ≈ {fromCurrency ? `${format(fromCurrency.rate, 4)} ${fromCurrency.code}` : "—"}</b>
                </>
              )}
            </div>
          </div>
        </div>

        {err && <div className="cc-error">{err}</div>}
      </div>

      {/* Searchable dropdown (portal-style) */}
      <CurrencyDropdown
        open={showFrom}
        onClose={() => setShowFrom(false)}
        currencies={currencies}
        onSelect={setFromCurrency}
        selectedCode={fromCurrency?.code}
      />
    </div>
  );
};

export default CurrencyConverter;
